/**
 * Persona Context Provider (F044)
 *
 * Provides persona-aware context throughout the app based on user roles.
 * Personas: engineering, security, auditor, executive
 */
import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { personaApi } from '../../services/api';
import type { PersonaType, UserPersona } from '../../types';

interface PersonaContextValue {
  persona: PersonaType;
  defaultRoute: string;
  capabilities: string[];
  isLoading: boolean;
  hasCapability: (cap: string) => boolean;
}

const PersonaCtx = createContext<PersonaContextValue>({
  persona: 'security',
  defaultRoute: '/home',
  capabilities: [],
  isLoading: true,
  hasCapability: () => false,
});

export const usePersona = () => useContext(PersonaCtx);

export const PersonaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, isLoading } = useQuery<UserPersona>({
    queryKey: ['persona'],
    queryFn: () => personaApi.getPersona(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const value = useMemo<PersonaContextValue>(() => ({
    persona: data?.persona ?? 'security',
    defaultRoute: data?.defaultRoute ?? '/home',
    capabilities: data?.capabilities ?? [],
    isLoading,
    hasCapability: (cap: string) => data?.capabilities?.includes(cap) ?? false,
  }), [data, isLoading]);

  return <PersonaCtx.Provider value={value}>{children}</PersonaCtx.Provider>;
};
